// ---------------------------------------------------------------------------
// The Do Not Contact list from the browser, over REST (R11c)
//
// The opt-out page and the wizard summary asked BaseQL over the socket
// (`suppressContact`, `countSuppressedEmails`). They call two routes now
// (entrypoints/sdr/src/server/routes/suppression.ts):
//
//   POST /api/opt-out            {token}      → {ok: true}; no account needed
//   POST /api/suppression/count  {emails: […]} → {ok: true, count}; behind the login
//
// The count is a read, but the leads' addresses go in the body and never in
// the URL: nginx in front of Node writes every request line to its log. The
// server takes at most 2,000 addresses per request, so a long lead list is
// sent 1,000 at a time. Each address is asked once, in one request, and the
// counts are added up. If any part fails, the whole count fails — a number
// that is too small would tell the user fewer people opted out than did.
//
// OPT1 (29.10.): the link in an email is /optout?t=<token>, the address sealed
// by the server; the page sends the token and never sees the address. Links
// sent before say ?email=<address>; the page sends {email} for those, which
// the server takes until 30 Jan 2027 (LEGACY_OPT_OUT_UNTIL) and then answers
// link_expired.
// ---------------------------------------------------------------------------

export const OPT_OUT_URL = '/api/opt-out';
export const SUPPRESSION_COUNT_URL = '/api/suppression/count';

/** What the opt-out page says when the server gives no reason of its own. */
export const OPT_OUT_FAILED = 'Something went wrong. Please try again.';

/** Addresses per count request; the server takes up to 2,000. */
export const COUNT_CHUNK_ADDRESSES = 1000;

/** What the link in an email hands the page: the sealed token, or, in a link sent before OPT1, the address. */
export type OptOutLink = { token: string } | { email: string };

/** Why the server will not take a link at all; trying again cannot help. */
export type OptOutLinkProblem = 'link_invalid' | 'link_expired';

/** A refused opt-out: the reason to show, and whether it is the link itself. */
export class OptOutFailed extends Error {
  readonly linkProblem: OptOutLinkProblem | null;

  constructor(message: string, linkProblem: OptOutLinkProblem | null) {
    super(message);
    this.name = 'OptOutFailed';
    this.linkProblem = linkProblem;
  }
}

/** The link in a page's query string: ?t= first, an old ?email= only without one, null for neither. */
export function readOptOutLink(search: string): OptOutLink | null {
  const params = new URLSearchParams(search);
  const token = (params.get('t') ?? '').trim();
  if (token) return { token };
  const email = (params.get('email') ?? '').trim();
  return email ? { email } : null;
}

/** The body of a JSON answer, or null when there is none. */
async function bodyOf(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await res.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Put the link's address on the Do Not Contact list. Resolves when the server
 * took it; otherwise throws an OptOutFailed with the server's own reason, or
 * the page's fallback, and the link's problem when the server names one.
 */
export async function optOut(link: OptOutLink): Promise<void> {
  let res: Response;
  try {
    res = await fetch(OPT_OUT_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify('token' in link ? { token: link.token } : { email: link.email }),
    });
  } catch {
    throw new OptOutFailed(OPT_OUT_FAILED, null);
  }
  const body = await bodyOf(res);
  if (res.ok && body?.ok === true) return;
  const reason = typeof body?.error === 'string' && body.error ? body.error : OPT_OUT_FAILED;
  const problem = body?.code === 'link_invalid' || body?.code === 'link_expired' ? body.code : null;
  throw new OptOutFailed(reason, problem);
}

/** The addresses to ask about: trimmed, lower-cased, each once, in parts of at most COUNT_CHUNK_ADDRESSES. */
export function countBatches(emails: readonly (string | null | undefined)[]): string[][] {
  const unique = [...new Set(emails.map(e => (e ?? '').trim().toLowerCase()).filter(Boolean))];
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += COUNT_CHUNK_ADDRESSES)
    batches.push(unique.slice(i, i + COUNT_CHUNK_ADDRESSES));
  return batches;
}

/** How many of these addresses are on the Do Not Contact list. Throws if any part of the count fails. */
export async function countSuppressedContacts(emails: readonly (string | null | undefined)[]): Promise<number> {
  const counts = await Promise.all(
    countBatches(emails).map(async batch => {
      const res = await fetch(SUPPRESSION_COUNT_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ emails: batch }),
      });
      const body = await bodyOf(res);
      if (!res.ok || body?.ok !== true || typeof body.count !== 'number') {
        throw new Error(typeof body?.error === 'string' ? body.error : `The count failed (${res.status})`);
      }
      return body.count;
    }),
  );
  return counts.reduce((sum, n) => sum + n, 0);
}
